'use client';

import localisePath from '@app/lib/helpers/localise-path';
import { Language } from '@app/lib/store-context/Context';
import { getContext } from '@app/lib/store-context/get-context';
import { hmacSignature } from '@arkadecx/arkade-cloud-functions-auth';
import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { Icon, Right } from 'mmds';
import { useParams } from 'next/navigation';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type MouseEvent,
} from 'react';
import s from './Teaser.module.css';

// Avoid SSR warning from useLayoutEffect; falls back to useEffect on the server.
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const TEASER_CURSOR_SRC = '/teaser/cursor_updated.svg';

/**
 * Props for `Teaser`.
 */
export type TeaserProps = SliceComponentProps<Content.TeaserSlice>;

type Clip = { url: string; cursorLabel: string; mobileTouchLabel: string };
type Phase = 'videos' | 'signup' | 'share';

function mediaUrlFromPrismicLinkField(field: unknown): string | undefined {
    if (!field || typeof field !== 'object') {
        return undefined;
    }
    if (
        'url' in field &&
        typeof field.url === 'string' &&
        field.url.length > 0
    ) {
        return field.url;
    }
    return undefined;
}

function clipsFromSlice(
    slice: Content.TeaserSlice,
    preferMobile: boolean
): Clip[] {
    return slice.primary.videos
        .map((item) => {
            const fallback = mediaUrlFromPrismicLinkField(item.video_clip);
            const mobile = mediaUrlFromPrismicLinkField(
                (item as unknown as Record<string, unknown>)?.mobile_video_clip
            );
            const url = preferMobile ? mobile || fallback : fallback;
            if (!url) {
                return null;
            }
            return {
                url,
                cursorLabel: item.cursor_label ?? '',
                mobileTouchLabel: item.mobile_touch_label ?? '',
            };
        })
        .filter((x): x is Clip => x !== null);
}

function imageUrlFromPrismicImageField(field: unknown): string | undefined {
    if (!field || typeof field !== 'object') {
        return undefined;
    }
    if ('url' in field && typeof field.url === 'string' && field.url.length) {
        return field.url;
    }
    return undefined;
}

function textFromPrismicTextField(field: unknown): string {
    if (typeof field === 'string') return field;
    if (field && typeof field === 'object' && 'value' in field) {
        const v = (field as { value?: unknown }).value;
        if (typeof v === 'string') return v;
    }
    return '';
}

const Teaser = ({ slice, context }: TeaserProps): JSX.Element | null => {
    // null = not yet measured. Deferred so we don't render the desktop variant
    // on first paint when the user is actually on a small screen.
    const [preferMobileMedia, setPreferMobileMedia] = useState<boolean | null>(
        null
    );
    // While pending, resolve mobile-first so `clips.length` isn't accidentally
    // 0 for slices that only define mobile URLs. No videos are actually
    // rendered until `viewportReady`, so this is purely a gating check.
    const clips = clipsFromSlice(slice, preferMobileMedia !== false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [displayIndex, setDisplayIndex] = useState(0);
    const [phase, setPhase] = useState<Phase>('videos');
    const [mounted, setMounted] = useState(false);
    const [customCursorEnabled, setCustomCursorEnabled] = useState(false);
    const [cursorVisible, setCursorVisible] = useState(false);
    const [cursorPos, setCursorPos] = useState<{ x: number; y: number }>({
        x: 0,
        y: 0,
    });
    const [shareBubbleOverride, setShareBubbleOverride] = useState<
        'COPIED!' | null
    >(null);
    const [message, setMessage] = useState<string | null>(null);
    // Desktop-only: fully downloaded clips as blob URLs for instant playback.
    // On mobile we stream via <video> instead — blob fetch
    // must complete the whole file before any frame can play.
    const [blobUrls, setBlobUrls] = useState<Record<number, string>>({});
    // Locked only after a clip starts playing so a blob can still replace the
    // streaming URL before the first frame.
    const [lockedSrcs, setLockedSrcs] = useState<Record<number, string>>({});
    // Per-clip readiness — once true, switching to this clip is instant.
    const [clipReady, setClipReady] = useState<Record<number, boolean>>({});
    const emailInput = useRef<HTMLInputElement | null>(null);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const shareBubbleResetRef = useRef<
        ReturnType<typeof setTimeout> | undefined
    >(undefined);

    const { lang: routerLang } = useParams();
    const region = getContext(
        (Array.isArray(routerLang) ? routerLang[0] : routerLang) as Language
    ).countryCode;

    const isActiveClipReady = Boolean(clipReady[activeIndex]);

    const advance = useCallback(() => {
        if (clips.length === 0) {
            return;
        }

        if (!clipReady[activeIndex]) {
            const el = videoRefs.current[activeIndex];
            if (el) {
                void el.play().catch(() => {
                    /* autoplay policies may block until gesture */
                });
            }
            return;
        }

        if (activeIndex < clips.length - 1 && !clipReady[activeIndex + 1]) {
            const next = videoRefs.current[activeIndex + 1];
            if (next) {
                try {
                    next.load();
                } catch {
                    /* no-op */
                }
            }
            return;
        }

        setActiveIndex((i) => {
            const isLast = i >= clips.length - 1;
            if (isLast) {
                setPhase('signup');
                return i;
            }
            return i + 1;
        });
    }, [activeIndex, clipReady, clips.length]);

    const activeClipUrl = clips[activeIndex]?.url;
    const clipUrlsKey = clips.map((c) => c.url).join('|');

    const markClipReady = useCallback((index: number) => {
        setClipReady((prev) => {
            if (prev[index]) {
                return prev;
            }
            return { ...prev, [index]: true };
        });
    }, []);

    const lockClipSrc = useCallback(
        (index: number) => {
            const clip = clips[index];
            if (!clip) {
                return;
            }
            setLockedSrcs((prev) => {
                if (prev[index]) {
                    return prev;
                }
                return {
                    ...prev,
                    [index]: blobUrls[index] ?? clip.url,
                };
            });
        },
        [blobUrls, clips]
    );

    // Track readiness for every clip so switches are instant once cached.
    useEffect(() => {
        if (phase !== 'videos') {
            return;
        }

        const cleanups: (() => void)[] = [];

        clips.forEach((_, i) => {
            if (blobUrls[i]) {
                markClipReady(i);
            }

            const el = videoRefs.current[i];
            if (!el) {
                return;
            }

            const markFromElement = () => {
                if (el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
                    markClipReady(i);
                }
            };

            const onPlaying = () => {
                markClipReady(i);
                lockClipSrc(i);
            };

            markFromElement();
            el.addEventListener('canplay', markFromElement);
            el.addEventListener('loadeddata', markFromElement);
            el.addEventListener('canplaythrough', markFromElement);
            el.addEventListener('playing', onPlaying);

            cleanups.push(() => {
                el.removeEventListener('canplay', markFromElement);
                el.removeEventListener('loadeddata', markFromElement);
                el.removeEventListener('canplaythrough', markFromElement);
                el.removeEventListener('playing', onPlaying);
            });
        });

        return () => {
            cleanups.forEach((cleanup) => cleanup());
        };
    }, [
        phase,
        clipUrlsKey,
        clips,
        blobUrls,
        markClipReady,
        lockClipSrc,
        preferMobileMedia,
    ]);

    useEffect(() => {
        setActiveIndex((i) => (clips.length > 0 && i >= clips.length ? 0 : i));
        setDisplayIndex((i) => (clips.length > 0 && i >= clips.length ? 0 : i));
    }, [clips.length]);

    useEffect(() => {
        if (clips.length === 0) {
            return;
        }
        if (phase !== 'videos') {
            videoRefs.current.forEach((el) => el?.pause());
            return;
        }

        const el = videoRefs.current[activeIndex];
        if (!el) return;

        const tryPlay = () => {
            try {
                el.currentTime = 0;
            } catch {
                /* ignore seek errors when metadata not ready */
            }
            void el.play().catch(() => {
                /* autoplay policies may block until gesture */
            });
        };

        if (el.readyState >= 2) {
            tryPlay();
        } else {
            const onReady = () => {
                el.removeEventListener('loadeddata', onReady);
                tryPlay();
            };
            el.addEventListener('loadeddata', onReady);
            try {
                el.load();
            } catch {
                /* no-op */
            }
            return () => el.removeEventListener('loadeddata', onReady);
        }
    }, [activeIndex, clips.length, phase, activeClipUrl]);

    // Promote the active video to the visible layer. Cached clips swap instantly;
    // uncached clips keep the previous frame visible until playback starts.
    useEffect(() => {
        if (phase !== 'videos') return;
        const el = videoRefs.current[activeIndex];
        if (!el) return;

        if (activeIndex === displayIndex) {
            return;
        }

        const promote = () => {
            setDisplayIndex(activeIndex);
            videoRefs.current.forEach((v, j) => {
                if (v && j !== activeIndex) {
                    v.pause();
                }
            });
        };

        if (clipReady[activeIndex]) {
            promote();
            return;
        }

        if (!el.paused && el.readyState >= 3) {
            promote();
            return;
        }

        el.addEventListener('playing', promote, { once: true });
        return () => el.removeEventListener('playing', promote);
    }, [activeIndex, clipReady, displayIndex, phase, activeClipUrl]);

    // Ensure mobile-visible clips always restart from frame zero.
    useEffect(() => {
        if (phase !== 'videos') return;
        if (!preferMobileMedia) return;
        const el = videoRefs.current[displayIndex];
        if (!el) return;

        const restartFromHead = () => {
            try {
                el.currentTime = 0;
            } catch {
                /* ignore seek errors when metadata not ready */
            }
            void el.play().catch(() => {
                /* autoplay policies may block until gesture */
            });
        };

        if (el.readyState >= 2) {
            restartFromHead();
        } else {
            const onReady = () => {
                el.removeEventListener('loadeddata', onReady);
                restartFromHead();
            };
            el.addEventListener('loadeddata', onReady);
            return () => el.removeEventListener('loadeddata', onReady);
        }
    }, [displayIndex, phase, preferMobileMedia]);

    useEffect(() => {
        setLockedSrcs({});
        setBlobUrls({});
        setClipReady({});
    }, [clipUrlsKey]);

    // Desktop: prefetch all clips as blobs immediately.
    // Mobile: stream clip 0 first, then sequentially cache the rest in the
    // background so every subsequent tap is instant.
    useEffect(() => {
        if (preferMobileMedia === null) return;
        if (clips.length === 0 || phase !== 'videos') return;
        if (preferMobileMedia && !clipReady[0]) return;

        const controller = new AbortController();
        const createdObjectUrls: string[] = [];

        const fetchClipBlob = async (index: number) => {
            const clip = clips[index];
            if (!clip) return;

            try {
                const response = await fetch(clip.url, {
                    signal: controller.signal,
                    credentials: 'omit',
                    cache: 'force-cache',
                });
                if (!response.ok) return;
                const blob = await response.blob();
                if (controller.signal.aborted) return;
                const objectUrl = URL.createObjectURL(blob);
                createdObjectUrls.push(objectUrl);
                setBlobUrls((prev) => {
                    if (lockedSrcs[index]) {
                        URL.revokeObjectURL(objectUrl);
                        return prev;
                    }
                    return { ...prev, [index]: objectUrl };
                });
                markClipReady(index);
            } catch {
                /* network or abort — fallback to streaming URL */
            }
        };

        void (async () => {
            const priorityOrder = preferMobileMedia
                ? clips.map((_, i) => i).filter((i) => i >= 1)
                : [0, 1, ...clips.map((_, i) => i).filter((i) => i > 1)];
            for (const i of priorityOrder) {
                if (controller.signal.aborted) break;
                await fetchClipBlob(i);
            }
        })();

        return () => {
            controller.abort();
            createdObjectUrls.forEach((url) => URL.revokeObjectURL(url));
            setBlobUrls({});
            setLockedSrcs({});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clipUrlsKey, preferMobileMedia, clipReady[0], phase]);

    // When a blob URL arrives, warm the matching <video> element immediately.
    useEffect(() => {
        if (phase !== 'videos') return;

        Object.keys(blobUrls).forEach((key) => {
            const index = Number(key);
            const el = videoRefs.current[index];
            if (!el || lockedSrcs[index]) {
                return;
            }
            if (index === activeIndex && !el.paused) {
                return;
            }
            try {
                el.load();
            } catch {
                /* no-op */
            }
        });
    }, [activeIndex, blobUrls, lockedSrcs, phase]);

    // Hint the browser to start fetching the first clip as early as possible.
    useEffect(() => {
        if (preferMobileMedia === null || clips.length === 0) return;
        const href = clips[0]?.url;
        if (!href) return;

        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'fetch';
        link.href = href;
        link.crossOrigin = 'anonymous';
        document.head.appendChild(link);
        return () => {
            link.remove();
        };
    }, [clipUrlsKey, preferMobileMedia]);

    const rawShareLink = textFromPrismicTextField(
        (slice.primary as unknown as Record<string, unknown>)?.share_link
    );
    const shareLink = rawShareLink
        ? localisePath(
              rawShareLink,
              (Array.isArray(routerLang)
                  ? routerLang[0]
                  : routerLang) as Language
          )
        : undefined;

    const handleCopyLink = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const url =
                shareLink ||
                (typeof window !== 'undefined' ? window.location.href : '');
            if (!url) {
                return;
            }
            void (async () => {
                try {
                    await navigator.clipboard.writeText(url);
                    // Always retrigger feedback on every click.
                    setShareBubbleOverride(null);
                    requestAnimationFrame(() => {
                        setShareBubbleOverride('COPIED!');
                    });
                    if (shareBubbleResetRef.current) {
                        clearTimeout(shareBubbleResetRef.current);
                    }
                    shareBubbleResetRef.current = setTimeout(() => {
                        setShareBubbleOverride(null);
                    }, 1500);
                } catch {
                    setShareBubbleOverride(null);
                }
            })();
        },
        [shareLink]
    );

    useEffect(() => {
        return () => {
            const shareBubbleTimer = shareBubbleResetRef.current;
            if (shareBubbleTimer) {
                clearTimeout(shareBubbleTimer);
            }
        };
    }, []);

    useEffect(() => {
        setMounted(true);
    }, []);

    const activeLabel = clips[activeIndex]?.cursorLabel ?? '';
    const activeMobileLabel =
        clips[activeIndex]?.mobileTouchLabel?.trim() ||
        clips[activeIndex]?.cursorLabel?.trim() ||
        '';
    const primary = slice.primary as unknown as Record<string, unknown>;
    const signupBg = preferMobileMedia
        ? imageUrlFromPrismicImageField(
              primary?.mobile_sign_up_background_image
          ) || imageUrlFromPrismicImageField(primary?.sign_up_background_image)
        : imageUrlFromPrismicImageField(primary?.sign_up_background_image);
    const shareBg = preferMobileMedia
        ? imageUrlFromPrismicImageField(
              primary?.mobile_share_link_background_image
          ) ||
          imageUrlFromPrismicImageField(primary?.share_link_background_image)
        : imageUrlFromPrismicImageField(primary?.share_link_background_image);
    const tags = textFromPrismicTextField(
        (slice.primary as unknown as Record<string, unknown>)?.tags
    );

    useEffect(() => {
        // Only enable on devices with a fine pointer (mouse/trackpad).
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(pointer: fine)');
        const apply = () => setCustomCursorEnabled(Boolean(mq.matches));
        apply();
        mq.addEventListener?.('change', apply);
        return () => mq.removeEventListener?.('change', apply);
    }, []);

    useIsomorphicLayoutEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(max-width: 767px), (pointer: coarse)');
        const apply = () => setPreferMobileMedia(Boolean(mq.matches));
        apply();
        mq.addEventListener?.('change', apply);
        return () => mq.removeEventListener?.('change', apply);
    }, []);

    const cursorText =
        phase === 'videos'
            ? activeLabel
            : phase === 'signup'
            ? 'BE THE FIRST TO KNOW.'
            : shareBubbleOverride ?? 'You’re in. Watch this space.';

    const mobilePromptText =
        phase === 'videos'
            ? !isActiveClipReady
                ? 'LOADING...'
                : activeMobileLabel
                ? activeMobileLabel
                : 'TAP TO CONTINUE'
            : '';

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            if (!customCursorEnabled) return;
            setCursorPos({ x: e.clientX, y: e.clientY });
            setCursorVisible(true);
        },
        [customCursorEnabled]
    );
    const onSubmit = useCallback(async () => {
        const emailAddress = (emailInput.current?.value || '').trim();
        if (!emailAddress) {
            setMessage('Please enter your email address.');
            return;
        }
        const tagsList = tags
            ? tags
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean)
            : [];

        const body = {
            emailAddress,
            region,
            tags: tagsList,
        };
        const route = '/api/services/newsletter-subscribe';
        try {
            const now = Date.now();
            const signature = await hmacSignature(
                process.env.NEXT_PUBLIC_API_SECRET,
                now,
                route,
                body
            );
            const response = await fetch(route, {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json',
                    'x-authentication': `${now}:${signature}`,
                },
                body: JSON.stringify(body),
            });
            const responseObject = (await response.json()) as {
                message?: string;
            };
            setMessage(responseObject?.message ?? null);
            if (response.ok) {
                setPhase('share');
            }
        } catch {
            setMessage('Something went wrong. Please try again.');
        }
    }, [region, tags]);

    if (clips.length === 0) {
        return null;
    }

    const mobileLayoutAttr: 'true' | 'false' | 'pending' =
        preferMobileMedia === null
            ? 'pending'
            : preferMobileMedia
            ? 'true'
            : 'false';
    const viewportReady = preferMobileMedia !== null;

    return (
        <section
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            data-custom-cursor={customCursorEnabled ? 'true' : 'false'}
            data-mobile-layout={mobileLayoutAttr}
            className={s.root}
            onMouseEnter={() => {
                if (customCursorEnabled) setCursorVisible(true);
            }}
            onMouseLeave={() => setCursorVisible(false)}
            onMouseMove={handleMouseMove}
        >
            {customCursorEnabled && cursorVisible && cursorText ? (
                <div
                    className={s.fakeCursor}
                    style={
                        {
                            left: `${cursorPos.x}px`,
                            top: `${cursorPos.y}px`,
                        } as React.CSSProperties
                    }
                    aria-hidden="true"
                >
                    <div
                        className={`${s.fakeCursorBubble} mmds-copy-three-detail`}
                    >
                        {cursorText}
                    </div>
                    <img
                        src={TEASER_CURSOR_SRC}
                        alt=""
                        className={s.fakeCursorIcon}
                    />
                </div>
            ) : null}
            {phase === 'videos' ? (
                <>
                    <div
                        className={`${s.stage} ${
                            !isActiveClipReady ? s.stageLoading : ''
                        }`}
                        onClick={advance}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                advance();
                            }
                        }}
                        role="button"
                        tabIndex={isActiveClipReady ? 0 : -1}
                        aria-busy={!isActiveClipReady}
                        aria-disabled={!isActiveClipReady}
                        aria-label={
                            isActiveClipReady
                                ? 'Play teaser clips. Activate to show the next clip.'
                                : 'Loading teaser clip. Please wait.'
                        }
                    >
                        {preferMobileMedia ? (
                            <div
                                className={s.mobileCursorPrompt}
                                aria-hidden="true"
                            >
                                <div
                                    className={`${s.mobileCursorPromptBubble} mmds-component-one `}
                                >
                                    <span className={s.mobileCursorPromptText}>
                                        {mobilePromptText}
                                    </span>
                                </div>
                            </div>
                        ) : null}
                        {viewportReady
                            ? clips.map((clip, i) => {
                                  const resolvedSrc =
                                      lockedSrcs[i] ?? blobUrls[i] ?? clip.url;
                                  return (
                                      <video
                                          key={`teaser-clip-${i}`}
                                          ref={(el) => {
                                              videoRefs.current[i] = el;
                                          }}
                                          className={`${s.videoLayer} ${
                                              i === displayIndex
                                                  ? s.videoLayerActive
                                                  : ''
                                          }`}
                                          src={resolvedSrc}
                                          autoPlay={i === activeIndex}
                                          muted
                                          playsInline
                                          webkit-playsinline="true"
                                          preload={
                                              blobUrls[i] ||
                                              clipReady[i] ||
                                              i === activeIndex ||
                                              i === activeIndex + 1
                                                  ? 'auto'
                                                  : preferMobileMedia
                                                  ? 'none'
                                                  : 'metadata'
                                          }
                                          loop
                                          controls={false}
                                      />
                                  );
                              })
                            : null}
                    </div>
                </>
            ) : null}

            {phase === 'signup' ? (
                <div className={s.panel}>
                    {signupBg ? (
                        <img src={signupBg} alt="" className={s.panelBg} />
                    ) : null}
                    <div className={s.panelOverlay}>
                        <div className={s.newsletterInputContainer}>
                            {mounted ? (
                                <input
                                    type="email"
                                    id="email"
                                    className={s.newsletterInput}
                                    placeholder="Enter email address"
                                    ref={emailInput}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            onSubmit();
                                        }
                                    }}
                                    autoComplete="email"
                                />
                            ) : null}
                            <button
                                type="button"
                                className={s.buttonIcon}
                                onClick={onSubmit}
                                onPointerDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    void onSubmit();
                                }}
                                aria-label="Submit email"
                            >
                                <span className="mmds-component-one">GO</span>
                                <Right />
                            </button>
                            {message ? (
                                <div className={s.message}>{message}</div>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {phase === 'share' ? (
                <div className={s.panel}>
                    {shareBg ? (
                        <img src={shareBg} alt="" className={s.panelBg} />
                    ) : null}
                    <div className={s.panelOverlay}>
                        <div className={s.shareWrap}>
                            <div className={s.shareBox}>
                                <div
                                    className={`${s.shareLeft} mmds-component-one`}
                                >
                                    <span className={s.shareLeftText}>
                                        {shareLink ||
                                            (typeof window !== 'undefined'
                                                ? window.location.href
                                                : '')}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className={s.shareRight}
                                    onClick={handleCopyLink}
                                    aria-label="Copy link"
                                >
                                    <span className="mmds-component-one">
                                        SHARE
                                    </span>
                                    <Icon icon="northeast" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
};

export default Teaser;
